import React, { useState, useEffect } from 'react';
import { Table, Spinner } from 'react-bootstrap';
import { fetchAPI } from '../utils/api';
import { postUsageData } from '../utils/usage';
import styles from './Home.module.css';
import TeamLogo from '../components/TeamLogo';
import TeamDropdown from '../components/TeamDropdown';
import EventDropdown from '../components/EventDropdown';
import { HomeMatch } from '../utils/types';

const mapColorClasses: { [key: string]: string } = {
  ABYSS: styles.mapButtonAbyss,
  ASCENT: styles.mapButtonAscent,
  BIND: styles.mapButtonBind,
  BREEZE: styles.mapButtonBreeze,
  HAVEN: styles.mapButtonHaven,
  SPLIT: styles.mapButtonSplit,
  PEARL: styles.mapButtonPearl,
  LOTUS: styles.mapButtonLotus,
  SUNSET: styles.mapButtonSunset,
  ICEBOX: styles.mapButtonIcebox,
  FRACTURE: styles.mapButtonFracture,
  CORRODE: styles.mapButtonCorrode,
};

const mapDisabledColorClasses: { [key: string]: string } = {
  ABYSS: styles.mapButtonAbyssDisabled,
  ASCENT: styles.mapButtonAscentDisabled,
  BIND: styles.mapButtonBindDisabled,
  BREEZE: styles.mapButtonBreezeDisabled,
  HAVEN: styles.mapButtonHavenDisabled,
  SPLIT: styles.mapButtonSplitDisabled,
  PEARL: styles.mapButtonPearlDisabled,
  LOTUS: styles.mapButtonLotusDisabled,
  SUNSET: styles.mapButtonSunsetDisabled,
  ICEBOX: styles.mapButtonIceboxDisabled,
  FRACTURE: styles.mapButtonFractureDisabled,
  CORRODE: styles.mapButtonCorrodeDisabled,
};

const Home: React.FC = () => {
  const [matches, setMatches] = useState<HomeMatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTeamKey, setSelectedTeamKey] = useState<string | null>(null);
  const [selectedEventKey, setSelectedEventKey] = useState<string | null>(null);
  const [isWideLayout, setIsWideLayout] = useState(window.innerWidth >= 768);
  const [openDropdown, setOpenDropdown] = useState<'team' | 'event' | null>(null);

  useEffect(() => {
    const handleResize = () => {
      setIsWideLayout(window.innerWidth >= 768);
    };

    window.addEventListener('resize', handleResize);
    
    // Post usage data for frontpage visit
    postUsageData('frontpage').catch(error => {
      console.error('Failed to post frontpage usage data:', error);
    });

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchMatches = async () => {
      try {
        setLoading(true);
        let endpoint = '/matches';
        const params = new URLSearchParams();
        
        if (selectedTeamKey) {
          params.append('team_key', selectedTeamKey);
        }
        if (selectedEventKey) {
          params.append('event_key', selectedEventKey);
        }
        
        if (params.toString()) {
          endpoint += `?${params.toString()}`;
        }
        
        const data = await fetchAPI(endpoint);
        const sortedMatches = data.matches.sort((a: HomeMatch, b: HomeMatch) => b.timestamp_utc - a.timestamp_utc);
        setMatches(sortedMatches);
      } catch (error) {
        console.error('Error fetching matches:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMatches();
  }, [selectedTeamKey, selectedEventKey]);

  const handleTeamSelect = (teamKey: string | null) => {
    setSelectedTeamKey(teamKey);
    setSelectedEventKey(null); // Reset event selection to "Any"
    setOpenDropdown(null); // Close the dropdown after selection
  };

  const handleEventSelect = (eventKey: string | null) => {
    setSelectedEventKey(eventKey);
    setSelectedTeamKey(null); // Reset team selection to "Any"
    setOpenDropdown(null); // Close the dropdown after selection
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = date.toLocaleString('default', { month: 'short' });
    const day = date.getDate();
    return { year, monthDay: `${month} ${day}${getOrdinalSuffix(day)}` };
  };

  const getOrdinalSuffix = (day: number) => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1:  return "st";
      case 2:  return "nd";
      case 3:  return "rd";
      default: return "th";
    }
  };

  const toggleTeamDropdown = () => {
    setOpenDropdown(openDropdown === 'team' ? null : 'team');
  };

  const toggleEventDropdown = () => {
    setOpenDropdown(openDropdown === 'event' ? null : 'event');
  };

  const truncateTeamName = (name: string) => {
    if (name.length > 24) {
      return name.substring(0, 24) + '...';
    }
    return name;
  };

  const isMapProcessed = (mapName: string, processedMapNames: string[] | undefined) => {
    return processedMapNames?.includes(mapName) || false;
  };

  return (
    <div className={styles.homeContainer}>
      <div className={styles.homeContent}>
        <div className="alert alert-warning text-center" role="alert">
          <i className="bi bi-exclamation-triangle-fill me-2" aria-hidden="true"></i>
          YouTube is now blocking our automated VOD downloads, so we’re unfortunately unable to process any new matches.
        </div>
        <div className={styles.filtersContainer}>
          <div className={styles.teamDropdownContainer}>
            <TeamDropdown 
              onSelect={handleTeamSelect} 
              selectedTeamKey={selectedTeamKey || undefined} 
              isOpen={openDropdown === 'team'}
              onToggle={toggleTeamDropdown}
            />
          </div>
          <div className={styles.eventDropdownContainer}>
            <EventDropdown 
              onSelect={handleEventSelect} 
              selectedEventKey={selectedEventKey || undefined} 
              isOpen={openDropdown === 'event'}
              onToggle={toggleEventDropdown}
            />
          </div>
        </div>
        <hr />
        {loading ? (
          <div className={styles.loadingContainer}>
            <div className={styles.loadingContent}>
              <Spinner animation="border" role="status" className={styles.spinner} />
              <span className={styles.loadingText}>Loading...</span>
            </div>
          </div>
        ) : matches.length > 0 ? (
          <Table className={styles.matchesTable}>
            <tbody>
              {matches.map(match => (
                <tr key={match.match_id}>
                  <td className={styles.dateColumn}>
                    {isWideLayout ? (
                      match.date_utc
                    ) : (
                      <>
                        <div>{formatDate(match.date_utc).year}</div>
                        <div>{formatDate(match.date_utc).monthDay}</div>
                      </>
                    )}
                  </td>
                  {isWideLayout ? (
                    <>
                      <td className={styles.teamColumn}>
                        <div className={styles.teamRow}>
                          <TeamLogo teamKey={match.teamA_key} />
                          <span className={styles.teamName}>{truncateTeamName(match.teamA_name)}</span>
                        </div>
                      </td>
                      <td className={styles.teamColumn}>
                        <div className={styles.teamRow}>
                          <TeamLogo teamKey={match.teamB_key} />
                          <span className={styles.teamName}>{truncateTeamName(match.teamB_name)}</span>
                        </div>
                      </td>
                    </>
                  ) : (
                    <td className={styles.teamsColumn}>
                      <div className={styles.teamRow}>
                        <TeamLogo teamKey={match.teamA_key} />
                        <span className={styles.teamName}>{truncateTeamName(match.teamA_name)}</span>
                      </div>
                      <div className={styles.teamRow}>
                        <TeamLogo teamKey={match.teamB_key} />
                        <span className={styles.teamName}>{truncateTeamName(match.teamB_name)}</span>
                      </div>
                    </td>
                  )}
                  <td className={styles.scoreColumn}>{match.teamA_score} - {match.teamB_score}</td>
                  <td className={styles.replayColumn}>
                    <a
                      href={`/match/${match.match_id}/1`}
                      className={`btn btn-primary btn-sm ${styles.replayButton}`}
                    >
                      <i className="bi bi-play-fill"></i>
                      <span>Replay</span>
                    </a>
                  </td>
                  <td>
                    {match.map_names.map((map_name, index) => {
                      const processed = isMapProcessed(map_name, match.processed_map_names);
                      return (
                        <a
                          href={processed ? `/match/${match.match_id}/${index + 1}` : undefined}
                          className={`btn btn-outline-primary ${styles.mapButton} ${
                            processed 
                              ? mapColorClasses[map_name] || ''
                              : `${mapDisabledColorClasses[map_name] || ''} ${styles.mapButtonDisabled}`
                          }`}
                          key={index}
                          onClick={processed ? undefined : (e) => e.preventDefault()}
                          style={processed ? {} : { cursor: 'not-allowed' }}
                        >
                          {map_name}
                        </a>
                      );
                    })}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <div className={styles.noMatchesContainer}>
            <p className={styles.noMatchesText}>No matches found. Try adjusting your filters.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Home;
